import { KeyRound, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'
import { useAuth0 } from '@/hooks/useAuth0'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

export default function UserMenu() {
  const navigate = useNavigate()
  const { user, logout, isClientAdmin } = useAuth()
  const { user: auth0User, logout: auth0Logout, isAuthenticated: isAuth0Authenticated } = useAuth0()

  // Use Auth0 user if authenticated via Auth0, otherwise fall back to legacy auth
  const currentUser = isAuth0Authenticated && auth0User ? {
    firstName: auth0User.firstName || auth0User.name?.split(' ')[0],
    lastName: auth0User.lastName || auth0User.name?.split(' ').slice(1).join(' '),
    email: auth0User.email,
    name: auth0User.name,
    role: auth0User.role,
  } : user ? {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
    role: undefined,
  } : null

  const handleLogout = isAuth0Authenticated ? auth0Logout : logout

  if (!currentUser) return null

  // Get user initials for avatar - matches Consumer Duty style
  const getUserInitials = () => {
    // Try name first
    const nameFromUser = currentUser.name?.trim()
    // Then try firstName + lastName
    const nameFromParts = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
    const nameToUse = nameFromUser || nameFromParts

    if (nameToUse && nameToUse.length > 0) {
      const nameParts = nameToUse.split(' ').filter(p => p.length > 0)
      const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || ''
      const secondInitial = nameParts.length > 1 ? nameParts[1]?.charAt(0).toUpperCase() : ''
      return firstInitial + secondInitial || firstInitial
    }

    // Fallback to email if no name
    if (currentUser.email) {
      return currentUser.email
        .split('@')[0]
        .substring(0, 2)
        .toUpperCase()
    }

    return '?'
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative h-8 w-8 rounded-full p-0 group focus:outline-none" aria-label="Open user menu">
          {/* Avatar with initials - matches Consumer Duty dark blue style */}
          <div className="h-8 w-8 rounded-full bg-[#172b4d] text-white text-sm font-medium flex items-center justify-center group-hover:bg-gray-500 transition-all duration-300">
            {getUserInitials()}
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64 mr-2">
        {/* User info header - matches Consumer Duty style */}
        <div className="px-4 py-4 border border-gray-200 bg-[#f5f6f8] rounded-md m-1">
          <div className="flex flex-row gap-x-2">
            <div>
              {/* Larger avatar in dropdown */}
              <div className="h-12 w-12 rounded-full bg-[#172b4d] text-white text-lg font-medium flex items-center justify-center">
                {getUserInitials()}
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="text-sm font-medium text-gray-900">
                {currentUser.name || 'User'}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {currentUser.email}
              </div>
              {currentUser.role && (
                <div className="text-xs text-gray-500 mt-0.5 font-medium">
                  {currentUser.role}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Activate Admin - only for users who are not yet a Client Admin (OR-312) */}
        {!isClientAdmin && (
          <DropdownMenuItem
            onClick={() => navigate('/activate-admin')}
            className="px-4 py-2 text-sm text-gray-900 cursor-pointer"
          >
            <KeyRound size={16} className="mr-2" />
            Activate Admin
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Logout */}
        <DropdownMenuItem
          onClick={() => handleLogout()}
          className="px-4 py-2 text-sm text-gray-900 cursor-pointer"
        >
          <LogOut size={16} className="mr-2" />
          Log Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
